import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Checklist, ChecklistItem, TaskColor} from 'yougile-model'
import {IconButtonNew} from '@components/common/base/buttons'
import {ProgressBar} from '@components/common/base/progress-bar/progress-bar'
import {IconTabler} from '@components/common/icon-tabler/icon-tabler'
import {useTaskCardStore} from '@components/task-card/task-card-store'
import {cn} from '@containers/html-utils'

const TwChecklistItem = tw.div`
  flex
  min-w-0
  items-start
  gap-4
`

const TwChecklistItemIcon = tw(IconTabler)<{$completed: boolean}>`
  flex-none
  ${p => (p.$completed ? 'text-status-success' : 'text-icon-secondary')}
`

const TwChecklistItemText = tw.div<{
  $completed: boolean
  $taskColor?: boolean
}>`
  min-w-0
  flex-1
  text-xxs-regular
  break-words
  ${p => {
    const textColor =
      p.$completed && !p.$taskColor ? 'text-placeholder-new' : 'text-primary'

    return p.$completed ? `${textColor} line-through` : textColor
  }}
`

interface ChecklistItemProps {
  title: string
  completed: boolean
  taskColor?: TaskColor
}

const ChecklistItemComponent = ({
  title,
  completed,
  taskColor
}: ChecklistItemProps) => (
  <TwChecklistItem>
    <TwChecklistItemIcon
      $completed={completed}
      icon={completed ? 'IconCheck' : 'IconPoint'}
      customSize={16}
      viewBox="0 0 24 24"
    />
    <TwChecklistItemText
      $taskColor={!!(taskColor && taskColor !== 'task-primary')}
      $completed={completed}
    >
      {title}
    </TwChecklistItemText>
  </TwChecklistItem>
)

const TwChecklist = tw.div`
  min-w-0
  flex
  flex-col
`

const TwChecklistTitle = tw.div`
  my-4
  text-placeholder-new
  text-10
  leading-4
  font-semibold
  uppercase
`

interface ChecklistProps {
  title: string
  items: ChecklistItem[]
  titleClassName?: string
  taskColor?: TaskColor
}

const ChecklistComponent = ({
  title,
  items,
  titleClassName,
  taskColor
}: ChecklistProps) => (
  <TwChecklist>
    <TwChecklistTitle className={titleClassName}>{title}</TwChecklistTitle>
    {items.map(x => (
      <ChecklistItemComponent
        key={x.id}
        title={x.title}
        completed={x.isCompleted}
        taskColor={taskColor}
      />
    ))}
  </TwChecklist>
)

const TwTaskChecklists = tw.div`
  group/task-checklists
`

const TwTaskChecklistsHeader = tw.div`
  flex
  gap-8
`

const TwChecklistToggleButton = tw(IconButtonNew)<{
  $isTaskColored?: boolean
  $isTaskCompleted?: boolean
}>`
  flex-none
  
  ${p => {
    if (p.$isTaskColored) {
      return `!text-icon-primary ${
        p.$isTaskCompleted
          ? 'opacity-[.38] group-hover/task-checklists:opacity-[.64]'
          : 'opacity-[.64] group-hover/task-checklists:opacity-100'
      }`
    }

    return '!text-secondary group-hover/task-checklists:text-action-default group-active/task-checklists:text-action-pressed'
  }}
`

interface TaskChecklistsHeader {
  checklists: Checklist[]
  expanded: boolean
  taskColor?: TaskColor
  isTaskCompleted?: boolean
}

const TaskChecklistsHeader = ({
  checklists,
  expanded,
  taskColor,
  isTaskCompleted
}: TaskChecklistsHeader) => {
  const {all, completed} = useMemo(() => {
    let completed = 0
    let all = 0
    checklists.forEach(xs => {
      completed += xs.items.filter(x => x?.isCompleted).length
      all += xs.items.length
    })
    return {all, completed}
  }, [checklists])

  const isTaskColored = taskColor && taskColor !== 'task-primary'

  return (
    <TwTaskChecklistsHeader>
      <ProgressBar
        className={cn(
          'flex-1',
          isTaskColored
            ? '!text-icon-primary'
            : all === completed && '!text-placeholder-new'
        )}
        value={completed}
        maxValue={all}
        lineColor={isTaskColored ? `progress-${taskColor}` : undefined}
      />
      <TwChecklistToggleButton
        icon={expanded ? 'IconChevronUp' : 'IconChevronDown'}
        $isTaskColored={!!(taskColor && taskColor !== 'task-primary')}
        $isTaskCompleted={isTaskCompleted}
      />
    </TwTaskChecklistsHeader>
  )
}

interface TaskChecklistBaseProps {
  checklists: Checklist[]
  expanded: boolean
  onToggle: (e: React.MouseEvent) => void
  className?: string
  titleClassName?: string
  taskColor?: TaskColor
  isTaskCompleted?: boolean
}

const TaskChecklistsBase = ({
  checklists,
  expanded,
  onToggle,
  className,
  titleClassName,
  taskColor,
  isTaskCompleted
}: TaskChecklistBaseProps) => (
  <TwTaskChecklists onClick={onToggle} className={className}>
    <TaskChecklistsHeader
      taskColor={taskColor}
      checklists={checklists}
      expanded={expanded}
      isTaskCompleted={isTaskCompleted}
    />

    {expanded &&
      checklists
        .filter(list => list.items.length)
        .map(list => (
          <ChecklistComponent
            key={list.id}
            title={list.title}
            items={list.items}
            titleClassName={titleClassName}
            taskColor={taskColor}
          />
        ))}
  </TwTaskChecklists>
)

export const TaskChecklists = observer(
  ({
    className,
    titleClassName,
    isTaskCompleted
  }: {
    coloredTask?: boolean
    isTaskCompleted?: boolean
    className?: string
    titleClassName?: string
  }) => {
    const [state, store] = useTaskCardStore()
    const checklists = state.checklists

    const [expanded, setExpanded] = useState(false)
    const onToggle = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        setExpanded(x => !x)
      },
      [setExpanded]
    )

    // There may be checklists w/o items, display the whole component only if at least one is not empty
    const notEmpty =
      !!checklists.length && checklists.some(x => !!x.items.length)

    return notEmpty ? (
      <TaskChecklistsBase
        onToggle={onToggle}
        expanded={expanded}
        checklists={checklists}
        className={className}
        titleClassName={titleClassName}
        taskColor={store.currentTask?.data.color}
        isTaskCompleted={isTaskCompleted}
      />
    ) : null
  }
)
